import { ColumnDef } from '@tanstack/react-table';
import { InventoryItem } from '../types/inventory';
import { Badge } from '../../../components/ui/badge';
import { format } from 'date-fns';
import { MoreHorizontal, FileEdit, History, ArrowUpDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { Button } from '../../../components/ui/button';

interface InventoryColumnActions {
  onEdit: (item: InventoryItem) => void;
  onViewHistory: (item: InventoryItem) => void;
}

export const columns = ({
  onEdit,
  onViewHistory,
}: InventoryColumnActions): ColumnDef<InventoryItem>[] => [
  {
    accessorKey: 'name',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          className="-ml-4"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Item Name
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return (
        <div className="font-medium">{row.getValue('name')}</div>
      )
    },
  },
  {
    accessorKey: 'category',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          className="-ml-4"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Category
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return (
        <Badge variant="secondary" className="font-normal">
          {row.getValue('category')}
        </Badge>
      )
    },
  },
  {
    accessorKey: 'location',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          className="-ml-4"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Location
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      )
    },
    cell: ({ row }) => {
      const location = row.getValue('location');
      return location ? (
        <Badge variant="outline" className="font-normal">
          {location as string}
        </Badge>
      ) : (
        <span className="text-muted-foreground">-</span>
      );
    },
  },
  {
    accessorKey: 'quantity',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          className="-ml-4"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Quantity
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      )
    },
    cell: ({ row }) => {
      const quantity = row.getValue('quantity') as number;
      const minThreshold = row.original.minThreshold;
      
      return (
        <div className="flex items-center gap-2">
          <span className="font-medium tabular-nums">
            {quantity} {row.original.unit}
          </span>
          {quantity <= minThreshold && (
            <Badge variant="destructive" className="animate-pulse">
              Low Stock
            </Badge>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'expirationDate',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          className="-ml-4"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Expiration
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      )
    },
    cell: ({ row }) => {
      const date = row.getValue('expirationDate') as string;
      if (!date) return <span className="text-muted-foreground">-</span>;
      
      const expiryDate = new Date(date);
      const today = new Date();
      const daysUntilExpiration = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      
      const isExpiringSoon = row.original.minExpirationThreshold && daysUntilExpiration <= row.original.minExpirationThreshold;
      
      return (
        <div className="flex items-center gap-2">
          <span className={isExpiringSoon ? "text-warning" : ""}>
            {format(expiryDate, 'MMM d, yyyy')}
          </span>
          {isExpiringSoon && (
            <Badge variant="warning" className="font-normal">
              {daysUntilExpiration} days left
            </Badge>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'lastUpdated',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          className="-ml-4"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Last Updated
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      )
    },
    cell: ({ row }) => {
      const date = row.getValue('lastUpdated') as string;
      if (!date) return <span className="text-muted-foreground">-</span>;
      
      return format(new Date(date), 'MMM d, yyyy');
    },
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      const item = row.original;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[160px]">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onEdit(item)}>
              <FileEdit className="mr-2 h-4 w-4" /> Edit Item
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onViewHistory(item)}>
              <History className="mr-2 h-4 w-4" /> View History
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
